
import {
  computed, defineComponent, reactive, ref, watch,
} from 'vue';

import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoEstoque from '@/servicos/Cadastros/Estoques/ServicoEstoque';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { INotaFiscalItemEstoque } from '@/models/Entidades/Fiscal/INotaFiscal';
import { IDTOProdutoEstoqueMovimento } from '@/models/DTO/Cadastros/Estoques/IDTOProdutoEstoqueMovimento';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import { useTelaBase } from '@/core/composables/TelaBase';

export default defineComponent({
  name: 'SelecionarEstoqueProdutoMovimento',
  props: {
    codigoProdutoDefinicao: {
      type: Number,
      required: true,
    },
    codigoUnidadePrincipal: {
      type: Number,
      required: true,
    },
    fatorConversaoUnidadePrincipal: {
      type: Number,
      required: true,
    },
    codigoUnidadeSelecionada: {
      type: Number,
      required: true,
    },
    fatorConversaoUnidadeSelecionada: {
      type: Number,
      required: true,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    quantidade: {
      type: Number,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    itensEstoque: {
      type: Array as () => INotaFiscalItemEstoque[],
      required: true,
    },
    estoquesMovimento: {
      type: Array as () => IDTOProdutoEstoqueMovimento[],
      default: [] as [],
    },
    movimentarEntrada: {
      type: Boolean,
      default: false,
    },
    movimentarSaida: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Selecione',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    classCss: {
      type: String,
      default: '',
    },
    processando: {
      type: Boolean,
      default: false,
    },
    apresentarEstoques: {
      type: Boolean,
      default: false,
    },
    identificacaoElemento: {
      type: String,
      default: '',
    },
    popOverHover: {
      type: Boolean,
      default: false,
    },
    inputFocus: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CampoNumerico,
  },
  emits: ['update:quantidade', 'update:apresentarEstoques', 'update:estoquesMovimento', 'update:popOverHover', 'update:inputFocus', 'update:itensEstoque', 'update:processando', 'update:movimentarEstoque', 'focus', 'blur'],
  setup(props, { emit }) {
    let debounceVerificarQuantidades = 0;

    const refSelecionarItemEstoque = ref<HTMLElement | null>(null);
    const servicoEstoque = new ServicoEstoque();
    const { apresentarMensagemAlerta } = useTelaBase();
    const state = reactive({
      buscandoDados: false,
      codigoEstoquePrincipal: 0,
    });

    const computedProcessando = computed({
      get: () => props.processando,
      set: (val: boolean) => {
        emit('update:processando', val);
      },
    });

    const computedApresentarEstoques = computed({
      get: () => props.apresentarEstoques,
      set: (val: boolean) => {
        emit('update:apresentarEstoques', val);
      },
    });

    const computedPopOverHover = computed({
      get: () => props.popOverHover,
      set: (val: boolean) => {
        emit('update:popOverHover', val);
      },
    });

    const computedInputFocus = computed({
      get: () => props.inputFocus,
      set: (val: boolean) => {
        emit('update:inputFocus', val);
      },
    });

    const computedItensEstoque = computed<INotaFiscalItemEstoque[]>({
      get: () => props.itensEstoque,
      set: (itens: INotaFiscalItemEstoque[]) => {
        emit('update:itensEstoque', itens);
      },
    });

    const computedEstoquesMovimento = computed({
      get: () => props.estoquesMovimento,
      set: (itens: IDTOProdutoEstoqueMovimento[]) => {
        emit('update:estoquesMovimento', itens);
      },
    });

    function zerarQuantidadesUtilizadas() {
      for (let index = 0; index < computedItensEstoque.value.length; index += 1) {
        computedItensEstoque.value[index].quantidade = 0;
      }
    }

    function estrategiaVerificacaoEstoque() {
      if (UtilitarioGeral.validaLista(computedItensEstoque.value) && UtilitarioGeral.validaLista(computedEstoquesMovimento.value)) {
        if (props.quantidade > 0) {
          if (props.movimentarSaida) {
            let quantidadeRestante = 0;
            if (props.codigoUnidadePrincipal === props.codigoUnidadeSelecionada) {
              quantidadeRestante = props.quantidade;
            } else {
              quantidadeRestante = (props.quantidade * (props.fatorConversaoUnidadeSelecionada / props.fatorConversaoUnidadePrincipal));
            }
            for (let index = 0; index < computedItensEstoque.value.length; index += 1) {
              if (quantidadeRestante > 0) {
                const estoque = computedEstoquesMovimento.value.find((c) => c.codigoItemEstoque === computedItensEstoque.value[index].codigoEstoqueItem);
                if (estoque !== undefined) {
                  if (!estoque.estoqueNegativo && estoque.quantidadeDisponivel > 0 && estoque.quantidadeDisponivel > quantidadeRestante) {
                    computedItensEstoque.value[index].quantidade = quantidadeRestante;
                    quantidadeRestante = 0;
                  } else if (!estoque.estoqueNegativo && estoque.quantidadeDisponivel > 0 && estoque.quantidadeDisponivel < quantidadeRestante) {
                    computedItensEstoque.value[index].quantidade = estoque.quantidadeDisponivel;
                    quantidadeRestante -= estoque.quantidadeDisponivel;
                  } else if (estoque.estoqueNegativo) {
                    computedItensEstoque.value[index].quantidade = quantidadeRestante;
                    quantidadeRestante -= quantidadeRestante;
                  }
                }
              } else {
                computedItensEstoque.value[index].quantidade = 0;
              }
            }

            if (quantidadeRestante > 0) {
              apresentarMensagemAlerta('Produto sem estoque disponível!');
              zerarQuantidadesUtilizadas();
              emit('update:quantidade', 0);
            }
          } else if (props.movimentarEntrada) {
            let qtdLancada = 0;

            computedItensEstoque.value.forEach((itemEstoque) => {
              qtdLancada += itemEstoque.quantidade;
            });
            const diferenca = props.quantidade - qtdLancada;
            if (computedItensEstoque.value.length > 0 && state.codigoEstoquePrincipal > 0) {
              if (computedItensEstoque.value.filter((c) => c.codigoEstoque === state.codigoEstoquePrincipal).length > 0) {
                computedItensEstoque.value[computedItensEstoque.value.findIndex((c) => c.codigoEstoque === state.codigoEstoquePrincipal)].quantidade += diferenca;
              }
            } else {
              computedItensEstoque.value[0].quantidade += diferenca;
            }
          } else {
            zerarQuantidadesUtilizadas();
          }
        }
      }
    }

    async function obterEstoquesProduto() {
      state.buscandoDados = true;
      computedProcessando.value = true;
      const estoques = await servicoEstoque.obterEstoquesProdutoMovimento(props.codigoProdutoDefinicao, props.empresas, props.movimentarEntrada, props.movimentarSaida);
      computedEstoquesMovimento.value = estoques;
      if (UtilitarioGeral.validaLista(estoques)) {
        estoques.forEach((estoque) => {
          if (estoque.estoquePrincipal === true) {
            state.codigoEstoquePrincipal = estoque.codigoEstoque;
          }
          if (props.movimentarSaida) {
            if (!computedItensEstoque.value.some((c) => c.codigoEstoqueItem === estoque.codigoItemEstoque)) {
              const notaFiscalItemEstoque: INotaFiscalItemEstoque = {} as INotaFiscalItemEstoque;
              notaFiscalItemEstoque.codigo = 0;
              notaFiscalItemEstoque.codigoNotaFiscalItem = 0;
              notaFiscalItemEstoque.codigoEmpresa = estoque.codigoEmpresa;
              notaFiscalItemEstoque.codigoEstoque = estoque.codigoEstoque;
              notaFiscalItemEstoque.codigoEstoqueItem = estoque.codigoItemEstoque;
              notaFiscalItemEstoque.codigoCentroCusto = estoque.codigoCentroCusto;
              notaFiscalItemEstoque.quantidade = 0;
              computedItensEstoque.value.push(notaFiscalItemEstoque);
            }
          } else if (props.movimentarEntrada) {
            if (!computedItensEstoque.value.some((c) => c.codigoEstoque === estoque.codigoEstoque)) {
              const notaFiscalItemEstoque: INotaFiscalItemEstoque = {} as INotaFiscalItemEstoque;
              notaFiscalItemEstoque.codigo = 0;
              notaFiscalItemEstoque.codigoNotaFiscalItem = 0;
              notaFiscalItemEstoque.codigoEmpresa = estoque.codigoEmpresa;
              notaFiscalItemEstoque.codigoEstoque = estoque.codigoEstoque;
              notaFiscalItemEstoque.codigoEstoqueItem = estoque.codigoItemEstoque;
              notaFiscalItemEstoque.codigoCentroCusto = estoque.codigoCentroCusto;
              notaFiscalItemEstoque.quantidade = 0;
              computedItensEstoque.value.push(notaFiscalItemEstoque);
            }
          }
        });

        estrategiaVerificacaoEstoque();
      }

      state.buscandoDados = false;
      computedProcessando.value = false;
    }

    function obterDescricaoEstoque(codigoEstoque: number): string {
      let descricaoEstoque = '';
      const itemEstoque = computedEstoquesMovimento.value.find((c) => c.codigoEstoque === codigoEstoque);
      if (itemEstoque !== undefined) {
        if (UtilitarioGeral.valorValido(itemEstoque.sigla)) {
          descricaoEstoque = `${itemEstoque.sigla} - `;
        }
        descricaoEstoque += itemEstoque.descricao;
      }
      return descricaoEstoque;
    }

    function obterCasasDecimaisEstoque(codigoItemEstoque: number): number {
      let casasDecimais = 0;
      const itemEstoque = computedEstoquesMovimento.value.find((c) => c.codigoItemEstoque === codigoItemEstoque);
      if (itemEstoque !== undefined) {
        casasDecimais = itemEstoque.casasDecimaisUnidade;
      }
      return casasDecimais;
    }

    function obterQuantidadeDisponivel(codigoItemEstoque: number): number {
      let quantidadeDisponivel = 0;
      const itemEstoque = computedEstoquesMovimento.value.find((c) => c.codigoItemEstoque === codigoItemEstoque);
      if (itemEstoque !== undefined) {
        quantidadeDisponivel = itemEstoque.quantidadeDisponivel;
      }
      return quantidadeDisponivel;
    }

    watch(async () => props.codigoProdutoDefinicao, async () => {
      if (props.codigoProdutoDefinicao > 0 && (!UtilitarioGeral.validaLista(computedItensEstoque.value) || props.codigoProdutoDefinicao !== props.estoquesMovimento[0].codigoProdutoDefinicao)) {
        computedItensEstoque.value = [] as INotaFiscalItemEstoque[];
        await obterEstoquesProduto();
      }
    }, { immediate: true });

    watch(async () => props.apresentarEstoques, async () => {
      if (props.apresentarEstoques && !state.buscandoDados && props.codigoProdutoDefinicao > 0 && !UtilitarioGeral.validaLista(computedEstoquesMovimento.value)) {
        await obterEstoquesProduto();
      }
    }, { immediate: true });

    function focus() {
      emit('focus');
    }

    function blur() {
      computedInputFocus.value = false;
      emit('blur');
    }

    function calcularQuantidades() {
      let totalQuantidade = 0;
      props.itensEstoque.forEach((itemEstoque) => {
        totalQuantidade += itemEstoque.quantidade;
      });

      if (totalQuantidade !== props.quantidade) {
        emit('update:quantidade', totalQuantidade);
      }
    }

    watch(() => props.quantidade, async () => {
      clearTimeout(debounceVerificarQuantidades);
      debounceVerificarQuantidades = setTimeout(() => {
        if (UtilitarioGeral.validaLista(computedEstoquesMovimento.value)) {
          estrategiaVerificacaoEstoque();
        }
      }, 600);
    }, { immediate: true });
    return {
      props,
      state,
      refSelecionarItemEstoque,
      computedItensEstoque,
      UtilitarioMascara,
      obterDescricaoEstoque,
      obterCasasDecimaisEstoque,
      obterQuantidadeDisponivel,
      focus,
      blur,
      calcularQuantidades,
      computedApresentarEstoques,
      computedPopOverHover,
      computedInputFocus,
    };
  },
});
